"use client";

import { useEffect } from "react";
import { useSession } from "next-auth/react";
import { runOfflineSync } from "@/lib/offline/sync";

// Once per page LOAD, not per mount — App Router remounts layout children on
// navigation, and the pipeline re-downloading the snapshot on every route
// change would be pure waste.
let syncedThisLoad = false;

/**
 * Registers the service worker and runs the offline sync pipeline in the
 * background. Rendered only in the signed-in shell; renders nothing.
 */
export function OfflineSync() {
  const { data: session } = useSession();
  const userId = session?.user.libraryId ? session.user.id : null;

  useEffect(() => {
    if (!userId) return;
    // The sync pipeline warm-boots /offline in a hidden iframe, and /offline
    // renders under this same root layout — without these guards the iframe
    // would start its own sync and recurse forever.
    if (window.self !== window.top) return;
    if (window.location.pathname === "/offline") return;
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js").catch(() => {});
    }
    const sync = () => {
      if (syncedThisLoad || !navigator.onLine) return;
      syncedThisLoad = true;
      // Wait for the worker to control fetches first — the pipeline's warm
      // boot of /offline only fills the chunk cache if its requests route
      // through the SW. Bounded so a stuck registration can't block forever.
      const ready =
        "serviceWorker" in navigator
          ? Promise.race([
              navigator.serviceWorker.ready,
              new Promise((r) => setTimeout(r, 5000)),
            ])
          : Promise.resolve();
      void ready.then(() => runOfflineSync(userId));
    };
    sync();
    window.addEventListener("online", sync);
    return () => window.removeEventListener("online", sync);
  }, [userId]);

  return null;
}
